"use client";

// Lua-original chat-chrome primitives — NOT from the @ai-elements registry.
//
// Three small pieces of conversation furniture both lua-admin and
// demo-website were reimplementing: a date divider between message groups,
// an inline per-message timestamp, and a grid of starter prompts shown in
// the empty-conversation state. Naming matches the AI Elements `Message*`
// + `Conversation*` conventions so they slot into existing compositions.

import type { ButtonHTMLAttributes, ComponentProps, ReactNode } from "react";
import { cn } from "@/lib/utils";

// --- ChatDateDivider ---------------------------------------------------

export type ChatDateDividerProps = ComponentProps<"div"> & {
  /** Label to show in the centered pill (e.g. "Today", "May 18"). */
  label: ReactNode;
};

export const ChatDateDivider = ({
  label,
  className,
  ...props
}: ChatDateDividerProps) => (
  // The hairlines are decorative; the label text IS the content.
  // Screen readers will announce the label as part of the normal reading
  // order, so we don't need an explicit role here.
  <div
    className={cn(
      "flex items-center gap-3 py-2 text-muted-foreground text-xs",
      className,
    )}
    data-slot="chat-date-divider"
    {...props}
  >
    <div aria-hidden="true" className="h-px flex-1 bg-border" />
    <span className="font-medium">{label}</span>
    <div aria-hidden="true" className="h-px flex-1 bg-border" />
  </div>
);

ChatDateDivider.displayName = "ChatDateDivider";

// --- MessageTimestamp --------------------------------------------------

export type MessageTimestampProps = Omit<ComponentProps<"time">, "children"> & {
  /** ISO string, Date, or number — formatted via `format` if provided. */
  value: string | number | Date;
  /**
   * Format function. Defaults to a short local time ("2:34 PM"). Override
   * for locale or precision (e.g. include seconds, drop AM/PM).
   */
  format?: (date: Date) => string;
};

const defaultFormat = (date: Date) =>
  date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

export const MessageTimestamp = ({
  value,
  format = defaultFormat,
  className,
  ...props
}: MessageTimestampProps) => {
  const date = value instanceof Date ? value : new Date(value);
  const iso = Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  const display = iso ? format(date) : "";

  return (
    <time
      className={cn(
        "font-normal text-muted-foreground text-xs tabular-nums",
        className,
      )}
      data-slot="message-timestamp"
      dateTime={iso}
      {...props}
    >
      {display}
    </time>
  );
};

MessageTimestamp.displayName = "MessageTimestamp";

// --- ConversationStarters ----------------------------------------------

export type ConversationStartersProps = ComponentProps<"div">;

/**
 * Vertical-on-mobile, grid-on-desktop layout for empty-state starter
 * prompts. Distinct from `Suggestions` (a horizontal chip strip): these
 * are larger prompt cards meant to anchor the welcome state.
 */
export const ConversationStarters = ({
  className,
  ...props
}: ConversationStartersProps) => (
  <div
    className={cn("flex flex-wrap gap-3", className)}
    data-slot="conversation-starters"
    {...props}
  />
);

ConversationStarters.displayName = "ConversationStarters";

export type ConversationStarterProps =
  ButtonHTMLAttributes<HTMLButtonElement> & {
    /** Optional leading icon — typically a Phosphor weight="fill" 16–20px. */
    icon?: ReactNode;
    /** Short label / heading line. */
    label: ReactNode;
    /** Optional second line of explanatory text. */
    description?: ReactNode;
  };

export const ConversationStarter = ({
  icon,
  label,
  description,
  className,
  ...props
}: ConversationStarterProps) => (
  <button
    className={cn(
      "group flex flex-col items-start gap-2 rounded-[14px] border border-border bg-card p-4 text-left",
      "transition-colors hover:bg-accent",
      "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none",
      "disabled:pointer-events-none disabled:opacity-50",
      className,
    )}
    data-slot="conversation-starter"
    type="button"
    {...props}
  >
    {icon ? (
      <span aria-hidden="true" className="text-primary">
        {icon}
      </span>
    ) : null}
    <span className="font-medium text-foreground text-sm">{label}</span>
    {description ? (
      <span className="font-normal text-muted-foreground text-sm">
        {description}
      </span>
    ) : null}
  </button>
);

ConversationStarter.displayName = "ConversationStarter";
